import { call, put, select } from 'redux-saga/effects';

import { fetchSendNodeAnalytics, getWorkflowCampaignNodes } from 'api/WorkflowsAPI';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { BackendNode, CampaignId, MetricInformation } from 'modules/workflows/types';
import { getErrorResponse } from 'utils/xhr';
import { GraphQLWorkflowNodeResourceFilter, TILE_METRICS_STUB } from '../../constants';
import { getSendMetricsRequestData } from '../../utils/dataUtils';
import { FetchSendMetricsRequestAction, actions } from '../actions';
import { getIsJourneyMetricsSelectionEnabled } from '../selectors';

export default function* fetchSendMetricsSaga(action: FetchSendMetricsRequestAction) {
  const { workflow, dateRange = {} } = action.payload;
  const errorMessage = 'Error fetching send metrics. Please try again later.';

  try {
    // GraphQL call to get nodes with campaigns (any send nodes basically)
    const { data, errors } = yield call(
      getWorkflowCampaignNodes,
      workflow?.id,
      workflow?.workflowType,
      [GraphQLWorkflowNodeResourceFilter.Campaign],
    );

    if (errors?.length) {
      yield put(actions.fetchSendMetricsError());
      yield put(addNegativeToastWithErrorId(errorMessage, 'fetchSendMetricsSaga'));
      return;
    }

    const campaigns = data?.workflow?.allNodes?.flatMap((node: BackendNode) => {
      const { resource } = node;

      if (!!resource && 'resources' in resource) {
        return (resource?.resources || []).map((channel) => channel.campaign) || [];
      }
      return resource?.campaign;
    });
    const isMetricsSelectionEnabled: boolean = yield select(getIsJourneyMetricsSelectionEnabled);
    const sendMetricsData = getSendMetricsRequestData({
      campaigns,
      workflow,
      dateRange,
      selectedMetrics: isMetricsSelectionEnabled ? workflow?.tileMetrics : TILE_METRICS_STUB,
    });

    // REST API call to fetch node analytics for existing campaigns
    const {
      campaignIdToMetrics,
    }: { campaignIdToMetrics: Record<CampaignId, MetricInformation[]> } = campaigns?.length
      ? yield call(fetchSendNodeAnalytics, sendMetricsData)
      : {};

    yield put(
      actions.fetchSendMetricsResponse(campaignIdToMetrics, {
        ...dateRange,
        startDateString: sendMetricsData.startTime,
        endDateString: sendMetricsData.endTime,
      }),
    );
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchSendMetricsError(err));
    yield put(addNegativeToastWithErrorId(errorMessage, 'fetchSendMetricsSaga'));
  }
}
